// Sign-out without localStorage.clear() (ADR 0017, PARITY D13): only our
// token key and our own `oase-admin:*` keys go; MSAL clears its own cache in
// logoutRedirect; the portal's keys survive.
import { currentRouterPath, loginPath, appBasePath } from './basePath'
import { getAccount, getMsal } from './msal'
import { clearSession } from './session'

export type SignOutReason = 'user' | 'expired'

export interface SignOutOptions {
  /** `user`: MSAL logoutRedirect. `expired`: back to the login page (D2). */
  reason: SignOutReason
  /** For `expired`: navigate to /login. Default true; RequireAuth passes false and redirects itself. */
  redirect?: boolean
}

export interface SignOutHooks {
  /** Called after the session is cleared: the integrator clears the query cache here. */
  onSignedOut: () => void
  /**
   * Navigate to a router-relative path (e.g. `/login?next=%2Ftenants`).
   * Default: a full page load of `APP_BASE_PATH + path`. The integrator may
   * plug in the router's navigate.
   */
  navigate: (path: string) => void
}

const defaultHooks: SignOutHooks = {
  onSignedOut: () => undefined,
  navigate: (path) => {
    window.location.assign(`${appBasePath()}${path.replace(/^\//, '')}`)
  }
}

let hooks: SignOutHooks = { ...defaultHooks }

/** Wire the integrator's hooks. Returns a reset to the defaults. */
export function configureSignOut(next: Partial<SignOutHooks>): () => void {
  hooks = { ...hooks, ...next }
  return () => {
    hooks = { ...defaultHooks }
  }
}

export async function signOut({ reason, redirect = true }: SignOutOptions): Promise<void> {
  let account = null
  try {
    account = getAccount()
  } catch {
    // MSAL unavailable: still end the session
  }
  clearSession()
  try {
    hooks.onSignedOut()
  } catch (error) {
    console.error('onSignedOut failed', error)
  }

  if (reason === 'user' && account) {
    const loginHint: unknown = account.idTokenClaims?.login_hint
    await getMsal().logoutRedirect({
      account,
      ...(typeof loginHint === 'string' && { logoutHint: loginHint })
    })
    return
  }
  if (!redirect) return
  const here = currentRouterPath()
  if (here.startsWith('/login')) return
  hooks.navigate(reason === 'expired' ? loginPath(here) : loginPath())
}
